import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Stack,
  Title,
  Group,
  Alert,
  Text,
  Badge,
  Skeleton,
  Select,
  Pagination,
  ActionIcon,
  Paper,
  TextInput,
  Tooltip,
  Table,
  ScrollArea,
  Anchor,
  SegmentedControl,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconRefresh,
  IconDatabase,
  IconChevronLeft,
  IconSearch,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import { Recipe } from '../types/mealPlan';
import {
  fetchCatalogRecipes,
  fetchCatalogCuisines,
  fetchTasteAxes,
  TasteAxisSummary,
} from '../services/adminCatalogService';
import {
  FLAVOR_KEYS,
  FLAVOR_LABELS,
  TEXTURE_LABELS,
  FORMAT_LABELS,
  CUISINE_LABELS,
  TECHNIQUE_KEYS,
  TECHNIQUE_LABELS,
  ingredientDisplayName,
} from '../types/tasteProfile';
import {
  AXIS_TYPE_LABELS,
  AXIS_TYPE_COLORS,
  BADGE_NO_TRUNCATE,
  axisDisplayName,
  axisStatus,
  AxisStatusBadge,
} from '../components/Admin/tasteAxisDisplay';

const PAGE_SIZE = 25;
const STORAGE_KEY = 'taste-axes-browser-state';

const isFlavorTag = (tag: string) =>
  (FLAVOR_KEYS as readonly string[]).includes(tag);
const isTechniqueTag = (tag: string) =>
  (TECHNIQUE_KEYS as readonly string[]).includes(tag);

/** Renders a group of axis values as badges, truncated with a +N overflow tooltip. */
const AxisCell = ({
  values,
  labels,
  color,
  max = 3,
}: {
  values: string[];
  labels: (v: string) => string;
  color: string;
  max?: number;
}) => {
  if (values.length === 0) {
    return <Text size="sm" c="dimmed">—</Text>;
  }
  const visible = values.slice(0, max);
  const hidden = values.slice(max);
  return (
    <Group gap={4} wrap="wrap">
      {visible.map((v) => (
        <Badge key={v} size="sm" variant="light" color={color} styles={BADGE_NO_TRUNCATE}>
          {labels(v)}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <Tooltip label={hidden.map(labels).join(', ')} multiline maw={300}>
          <Badge size="sm" variant="outline" color="gray" styles={BADGE_NO_TRUNCATE}>
            +{hidden.length}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
};

const formatDate = (iso?: string | null) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '—';
};

const loadSavedState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const TasteAxesPage = () => {
  usePageTitle('Taste Axes | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const saved = useRef(loadSavedState());

  const [view, setView] = useState<'recipes' | 'axes'>(saved.current?.view ?? 'recipes');

  // Axis overview state
  const [axes, setAxes] = useState<TasteAxisSummary[] | null>(null);
  const [axesLoading, setAxesLoading] = useState(false);
  const [axisTypeFilter, setAxisTypeFilter] = useState<string | null>(
    saved.current?.axisTypeFilter ?? null
  );
  const [axisStatusFilter, setAxisStatusFilter] = useState<string | null>(
    saved.current?.axisStatusFilter ?? null
  );

  // Recipe table state
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [cuisine, setCuisine] = useState<string | null>(saved.current?.cuisine ?? null);
  const [difficulty, setDifficulty] = useState<string | null>(saved.current?.difficulty ?? null);
  const [search, setSearch] = useState(saved.current?.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(saved.current?.search ?? '');
  const [cuisineOptions, setCuisineOptions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(saved.current?.page ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchCatalogCuisines(user).then(setCuisineOptions).catch(() => {});
  }, [user]);

  const loadAxes = useCallback(async () => {
    if (!user?.isAdmin) return;
    setAxesLoading(true);
    try {
      setAxes(await fetchTasteAxes(user));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load taste axes',
        color: 'red',
      });
    } finally {
      setAxesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (view === 'axes' && axes === null) loadAxes();
  }, [view, axes, loadAxes]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadPage = useCallback(async (page: number) => {
    if (!user?.isAdmin) return;
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const { recipes: data, total } = await fetchCatalogRecipes(user, {
        limit: PAGE_SIZE,
        offset,
        cuisine: cuisine || undefined,
        difficulty: difficulty || undefined,
        search: debouncedSearch || undefined,
      });
      setRecipes(data);
      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load catalog recipes',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [user, cuisine, difficulty, debouncedSearch]);

  // Persist filter/pagination state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      view, axisTypeFilter, axisStatusFilter, cuisine, difficulty, search, page: currentPage,
    }));
  }, [view, axisTypeFilter, axisStatusFilter, cuisine, difficulty, search, currentPage]);

  // Build a stable key from filter values to detect actual filter changes
  const filterKey = `${cuisine}|${difficulty}|${debouncedSearch}`;
  const prevFilterKey = useRef(filterKey);
  const hasLoadedInitialPage = useRef(false);

  useEffect(() => {
    // Gate on isAdmin, not just user: useAppUser emits the Firebase user
    // before the backend profile (which carries isAdmin) has loaded. Consuming
    // hasLoadedInitialPage on that first emission would spend the one-shot
    // marker on a load that loadPage's admin guard rejects, leaving the page
    // stuck on skeletons after the profile arrives.
    if (!user?.isAdmin) return;
    const filtersChanged = prevFilterKey.current !== filterKey;
    prevFilterKey.current = filterKey;

    if (!hasLoadedInitialPage.current) {
      hasLoadedInitialPage.current = true;
      loadPage(currentPage);
    } else if (filtersChanged) {
      setCurrentPage(1);
      loadPage(1);
    }
  }, [user, filterKey, loadPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadPage(page);
    window.scrollTo({ top: 0 });
  };

  const isLoading = userLoading || loading;

  if (!userLoading && !user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Access Denied" color="red" radius="lg">
          You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  const matchesStatusFilter = (a: TasteAxisSummary): boolean => {
    const status = axisStatus(a);
    switch (axisStatusFilter) {
      case 'stored':
        return a.exists;
      case 'missing':
        return !a.exists;
      case 'attention':
        return status !== 'active';
      default:
        return true;
    }
  };

  const filteredAxes = (axes ?? []).filter(
    (a) => (!axisTypeFilter || a.type === axisTypeFilter) && matchesStatusFilter(a)
  );
  const storedCount = (axes ?? []).filter((a) => a.exists).length;

  const renderAxesView = () => (
    <>
      <Paper p="md" radius="lg" withBorder>
        <Group gap="sm">
          <Select
            placeholder="Axis type"
            clearable
            value={axisTypeFilter}
            onChange={setAxisTypeFilter}
            data={Object.entries(AXIS_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            w={{ base: '100%', sm: 180 }}
          />
          <Select
            placeholder="Status"
            clearable
            value={axisStatusFilter}
            onChange={setAxisStatusFilter}
            data={[
              { value: 'stored', label: 'Stored' },
              { value: 'missing', label: 'Missing' },
              { value: 'attention', label: 'Needs attention' },
            ]}
            w={{ base: '100%', sm: 170 }}
          />
          {axes !== null && (
            <Text size="sm" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap' }}>
              {filteredAxes.length.toLocaleString()} shown · {storedCount.toLocaleString()} of{' '}
              {axes.length.toLocaleString()} stored
            </Text>
          )}
        </Group>
      </Paper>

      {axesLoading || axes === null ? (
        <Stack gap="xs">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={36} radius="sm" />
          ))}
        </Stack>
      ) : filteredAxes.length === 0 ? (
        <Alert icon={<IconDatabase size="1rem" />} title="No axes" color="blue" radius="lg">
          No axes match the current filters.
        </Alert>
      ) : (
        <Paper radius="lg" withBorder>
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                {/* On phones, keep only Axis / Status / Recipes: the axisId
                    line under each name already carries the type, and the
                    rebuild details live on the drill-down page */}
                <Table.Tr>
                  <Table.Th>Axis</Table.Th>
                  <Table.Th visibleFrom="sm">Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Recipes</Table.Th>
                  <Table.Th visibleFrom="sm">Rebuild floor</Table.Th>
                  <Table.Th visibleFrom="sm">Last rebuilt</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredAxes.map((axis) => (
                  <Table.Tr
                    key={axis.axisId}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate(`/admin/taste-axes/${encodeURIComponent(axis.axisId)}`)
                    }
                  >
                    <Table.Td>
                      <Text size="sm" fw={500} c={axis.exists ? undefined : 'dimmed'}>
                        {axisDisplayName(axis.type, axis.name)}
                      </Text>
                      {/* The raw axisId is one long unbreakable token that
                          forces the column wide, so phones swap it for a
                          compact type badge (replacing the hidden Type column) */}
                      <Text size="xs" c="dimmed" visibleFrom="sm">{axis.axisId}</Text>
                      <Badge
                        size="xs"
                        variant="light"
                        color={AXIS_TYPE_COLORS[axis.type] ?? 'gray'}
                        hiddenFrom="sm"
                        mt={2}
                        styles={BADGE_NO_TRUNCATE}
                      >
                        {AXIS_TYPE_LABELS[axis.type] ?? axis.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td visibleFrom="sm">
                      <Badge
                        size="sm"
                        variant="light"
                        color={AXIS_TYPE_COLORS[axis.type] ?? 'gray'}
                        styles={BADGE_NO_TRUNCATE}
                      >
                        {AXIS_TYPE_LABELS[axis.type] ?? axis.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <AxisStatusBadge axis={axis} size={isMobile ? 'xs' : 'sm'} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Text size="sm">{axis.liveRecipeCount.toLocaleString()}</Text>
                        {/* Hover-only detail, so phones drop it (it's on the
                            drill-down page) — and the saved width is what lets
                            the three phone columns fit without sideways scroll */}
                        {axis.storedRecipeCount !== null &&
                          axis.storedRecipeCount !== axis.liveRecipeCount && (
                            <Tooltip label={`${axis.storedRecipeCount.toLocaleString()} at the last rebuild`}>
                              <Text size="xs" c="dimmed" visibleFrom="sm">
                                ({axis.storedRecipeCount.toLocaleString()})
                              </Text>
                            </Tooltip>
                          )}
                      </Group>
                    </Table.Td>
                    <Table.Td visibleFrom="sm">
                      <Text size="sm" c="dimmed">{axis.minRecipeCount}</Text>
                    </Table.Td>
                    <Table.Td visibleFrom="sm">
                      <Text size="sm" c="dimmed">{axis.exists ? formatDate(axis.updatedAt) : '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
    </>
  );

  const renderRecipesView = () => (
    <>
      <Paper p="md" radius="lg" withBorder>
        <Group gap="sm">
          <TextInput
            placeholder="Search recipes..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 250 }}
          />
          <Select
            placeholder="Cuisine"
            clearable
            searchable
            value={cuisine}
            onChange={setCuisine}
            data={cuisineOptions}
            w={{ base: '100%', sm: 200 }}
          />
          <Select
            placeholder="Difficulty"
            clearable
            value={difficulty}
            onChange={setDifficulty}
            data={[
              { value: 'easy', label: 'Easy' },
              { value: 'medium', label: 'Medium' },
              { value: 'hard', label: 'Hard' },
            ]}
            w={{ base: '100%', sm: 140 }}
          />
          {totalCount !== null && (
            <Text size="sm" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap' }}>
              {totalCount.toLocaleString()} {totalCount === 1 ? 'recipe' : 'recipes'}
            </Text>
          )}
        </Group>
      </Paper>

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} height={48} radius="sm" />
          ))}
        </Stack>
      ) : recipes.length === 0 ? (
        <Alert icon={<IconDatabase size="1rem" />} title="No recipes" color="blue" radius="lg">
          No catalog recipes match the current filters.
        </Alert>
      ) : (
        <Stack gap="md">
          <Paper radius="lg" withBorder>
            <ScrollArea>
              <Table striped highlightOnHover style={{ minWidth: 1200 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Recipe</Table.Th>
                    <Table.Th>Cuisine</Table.Th>
                    <Table.Th>Flavor</Table.Th>
                    <Table.Th>Texture</Table.Th>
                    <Table.Th>Format</Table.Th>
                    <Table.Th>Technique</Table.Th>
                    <Table.Th>Ingredients</Table.Th>
                    <Table.Th>Dietary</Table.Th>
                    <Table.Th>Difficulty</Table.Th>
                    <Table.Th>Uses</Table.Th>
                    <Table.Th>Added</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recipes.map((recipe) => {
                    const tags = recipe.tags ?? [];
                    const flavorTags = tags.filter(isFlavorTag);
                    const techniqueTags = tags.filter(isTechniqueTag);
                    return (
                      <Table.Tr key={recipe.id}>
                        <Table.Td maw={240}>
                          <Anchor
                            size="sm"
                            fw={500}
                            lineClamp={2}
                            onClick={() => navigate(`/admin/catalog-recipe/${recipe.id}`)}
                          >
                            {recipe.name}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          {recipe.cuisine ? (
                            <Badge
                              size="sm"
                              variant="light"
                              color={AXIS_TYPE_COLORS.cuisine}
                              styles={BADGE_NO_TRUNCATE}
                            >
                              {CUISINE_LABELS[recipe.cuisine.toLowerCase()] ?? recipe.cuisine}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <AxisCell
                            values={flavorTags}
                            labels={(v) => FLAVOR_LABELS[v] ?? v}
                            color={AXIS_TYPE_COLORS.flavor}
                          />
                        </Table.Td>
                        <Table.Td>
                          <AxisCell
                            values={recipe.textureTags ?? []}
                            labels={(v) => TEXTURE_LABELS[v] ?? v}
                            color={AXIS_TYPE_COLORS.texture}
                          />
                        </Table.Td>
                        <Table.Td>
                          <AxisCell
                            values={recipe.formatTags ?? []}
                            labels={(v) => FORMAT_LABELS[v] ?? v}
                            color={AXIS_TYPE_COLORS.format}
                          />
                        </Table.Td>
                        <Table.Td>
                          <AxisCell
                            values={techniqueTags}
                            labels={(v) => TECHNIQUE_LABELS[v] ?? v}
                            color={AXIS_TYPE_COLORS.technique}
                          />
                        </Table.Td>
                        <Table.Td maw={220}>
                          <AxisCell
                            values={recipe.ingredientKeys ?? []}
                            labels={ingredientDisplayName}
                            color={AXIS_TYPE_COLORS.ingredient}
                          />
                        </Table.Td>
                        <Table.Td>
                          <AxisCell
                            values={recipe.dietaryRestrictions ?? []}
                            labels={(v) => v}
                            color={AXIS_TYPE_COLORS.dietary}
                            max={2}
                          />
                        </Table.Td>
                        <Table.Td>
                          {recipe.difficulty ? (
                            <Badge
                              size="sm"
                              variant="light"
                              color={
                                recipe.difficulty === 'easy'
                                  ? 'green'
                                  : recipe.difficulty === 'medium'
                                    ? 'yellow'
                                    : 'red'
                              }
                              styles={BADGE_NO_TRUNCATE}
                            >
                              {recipe.difficulty}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{recipe.usageCount ?? 0}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {formatDate(recipe.createdAt)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
          {totalPages > 1 && !debouncedSearch && (
            <Group justify="center">
              {/* Fewer, smaller controls on phones so the pager fits one row
                  instead of wrapping into ragged lines */}
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={handlePageChange}
                siblings={isMobile ? 0 : 1}
                size={isMobile ? 'sm' : 'md'}
              />
            </Group>
          )}
        </Stack>
      )}
    </>
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Group justify="space-between" align="flex-start">
            {/* nowrap, or the title stack drops below the chevron when the
                subtitle + right-side controls exceed one line */}
            <Group wrap="nowrap">
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/admin')}>
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Stack gap={0}>
                <Title order={2}>Taste Axes</Title>
                <Text c="dimmed" size="sm">
                  Per-recipe taste attributes and the stored axis embeddings built from them
                </Text>
              </Stack>
            </Group>
            <Group gap="sm">
              <SegmentedControl
                value={view}
                onChange={(v) => setView(v as 'recipes' | 'axes')}
                data={[
                  { value: 'recipes', label: 'Recipes' },
                  { value: 'axes', label: 'Axis overview' },
                ]}
              />
              <ActionIcon
                variant="outline"
                size="lg"
                title="Refresh"
                onClick={() => (view === 'axes' ? loadAxes() : loadPage(currentPage))}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Group>
          </Group>
        </div>

        {view === 'axes' ? renderAxesView() : renderRecipesView()}
      </Stack>
    </Container>
  );
};
